import { useEffect, useState } from "react";
import Seo from "@/components/Seo";
import PageHero from "@/components/site/PageHero";
import { Reveal, Overline } from "@/components/motion";
import { useEnquiry } from "@/context/EnquiryContext";
import { ArrowRight } from "lucide-react";
import { getPage } from "@/lib/api";

const DEFAULT_SECTIONS = [
  { key: "introduction", title: "Introduction", body: "India is not a country you visit — it is a country you feel.", image: "https://images.pexels.com/photos/16497359/pexels-photo-16497359.jpeg?auto=compress&cs=tinysrgb&w=1400" },
];

const DEFAULT_FACTS = [
  { label: "Languages", value: "22 official, 100s spoken — English is widely understood." },
  { label: "Currency", value: "Indian Rupee (INR). ATMs & cards widely accepted in cities." },
];

export default function AboutIndia() {
  const { openEnquiry } = useEnquiry();
  const [page, setPage] = useState(null);

  useEffect(() => { getPage("about-india").then(setPage); }, []);

  const sections = page?.sections?.length ? page.sections : DEFAULT_SECTIONS;
  const facts = page?.facts?.length ? page.facts : DEFAULT_FACTS;

  return (
    <>
      <Seo
        title={page?.seo_title || "About India | A Travel Guide for Visitors Worldwide"}
        description={page?.seo_description || "Everything you need to know about travelling to India — geography, climate, culture, food, festivals, visa, currency, transport and safety."}
      />
      <PageHero
        title={page?.hero_title || "About Incredible India"}
        subtitle={page?.hero_subtitle || "Culture • Heritage • Wildlife • Spirituality • Luxury • Nature"}
        image={page?.hero_image || "https://images.unsplash.com/photo-1564507592333-c60657eea523?auto=format&fit=crop&w=1600&q=80"}
        crumbs={[{ label: "About India" }]}
      />

      {/* Editorial sections */}
      <section className="py-16 sm:py-24 bg-[#fdfbf7]" data-testid="about-sections">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 space-y-20 sm:space-y-28">
          {sections.map((s, i) => (
            <Reveal key={s.key || i}>
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="relative">
                  <div className="overflow-hidden border border-[#d4af37]/30 aspect-[4/3]"><img src={s.image || s.img} alt={s.title} loading="lazy" className="w-full h-full object-cover" /></div>
                  <div className="absolute -inset-3 border border-[#d4af37]/20 pointer-events-none -z-10" />
                </div>
                <div>
                  <Overline>{String(i + 1).padStart(2, "0")}</Overline>
                  <h2 className="font-serif text-3xl sm:text-5xl text-[#3b1a1a] leading-tight mt-4">{s.title}</h2>
                  <p className="text-[#4a4a4a] text-lg leading-relaxed mt-5 whitespace-pre-line">{s.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Quick facts */}
      <section className="py-16 sm:py-24 bg-[#14523d] text-white" data-testid="about-facts">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-3 mb-10"><span className="h-px w-10 bg-[#d4af37]" /><span className="text-[11px] tracking-[0.34em] uppercase text-[#e8c970] font-semibold">Good to Know</span></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#d4af37]/20 border border-[#d4af37]/20">
            {facts.map((f, i) => (
              <div key={i} className="bg-[#14523d] p-7">
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#e8c970]">{f.label}</div>
                <div className="font-serif text-xl mt-2 leading-snug">{f.value}</div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <button data-testid="about-cta" onClick={() => openEnquiry()} className="inline-flex items-center gap-3 bg-[#d4af37] hover:bg-[#e8c970] text-[#2d1616] px-9 py-4 text-xs tracking-[0.26em] uppercase font-semibold transition-colors">{page?.cta_button || "Start Planning"} <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      </section>
    </>
  );
}
